import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Building2, Phone, Mail, MapPin, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getCompanyInfo, adminUpdateCompany } from '../api/api';

export const AdminCompany = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    tagline: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    stat_years: '',
    stat_clients: '',
    stat_response: ''
  });

  useEffect(() => {
    fetchCompany();
  }, []);

  const fetchCompany = async () => {
    try {
      const data = await getCompanyInfo();
      setFormData({
        name: data.name,
        city: data.city,
        tagline: data.tagline,
        description: data.description,
        phone: data.phone,
        email: data.email,
        address: data.address
      });
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await adminUpdateCompany(formData);
      toast.success('Informations mises à jour avec succès');
      fetchCompany();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Informations de l'entreprise</h2>
        <p className="text-gray-600">Modifiez les coordonnées affichées sur le site</p>
      </div>

      <Card className="border-0 shadow-lg">
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">Nom de l'entreprise *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">Ville *</Label>
                <Input
                  id="city"
                  value={formData.city}
                  onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="tagline">Slogan *</Label>
              <Input
                id="tagline"
                value={formData.tagline}
                onChange={(e) => setFormData(prev => ({ ...prev, tagline: e.target.value }))}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                required
                rows={4}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="phone">Téléphone *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+32 4 123 45 67"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Adresse complète *</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                placeholder="Rue Example 123, 4000 Liège, Belgique"
                required
              />
            </div>

            <div className="border-t border-gray-200 pt-5 space-y-4">
              <div>
                <h4 className="font-semibold text-gray-900">Chiffres affichés en haut du site</h4>
                <p className="text-sm text-gray-500 mt-1">
                  Laissez un champ vide pour ne pas afficher le chiffre correspondant.
                  Mieux vaut n'en annoncer aucun qu'en annoncer un que vous ne pourriez
                  pas justifier : une affirmation invérifiable est contestable par un
                  concurrent.
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="stat_years">Années de métier</Label>
                  <Input
                    id="stat_years"
                    value={formData.stat_years}
                    onChange={(e) => setFormData(prev => ({ ...prev, stat_years: e.target.value }))}
                    placeholder="30"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stat_clients">Clients satisfaits</Label>
                  <Input
                    id="stat_clients"
                    value={formData.stat_clients}
                    onChange={(e) => setFormData(prev => ({ ...prev, stat_clients: e.target.value }))}
                    placeholder="ex. 200+"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="stat_response">Délai de réponse</Label>
                  <Input
                    id="stat_response"
                    value={formData.stat_response}
                    onChange={(e) => setFormData(prev => ({ ...prev, stat_response: e.target.value }))}
                    placeholder="24h"
                  />
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 px-8 w-full sm:w-auto"
              >
                {saving ? (
                  <span className="flex items-center justify-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </span>
                ) : (
                  <span className="flex items-center justify-center">
                    <Building2 className="w-4 h-4 mr-2" />
                    Sauvegarder les modifications
                  </span>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Preview Section */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Aperçu</h3>
          <div className="space-y-2 text-sm">
            <p><strong>{formData.name}</strong> - {formData.city}</p>
            <p className="text-gray-600 italic">{formData.tagline}</p>
            <p className="text-gray-600">{formData.description}</p>
            <div className="pt-2 space-y-1 text-gray-700">
              <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-gray-400" aria-hidden="true" /> {formData.phone}</p>
              <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-gray-400" aria-hidden="true" /> {formData.email}</p>
              <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" aria-hidden="true" /> {formData.address}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
